import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useInView } from 'react-intersection-observer';
import { Check, ChevronsUpDown, Loader2, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PageableResponse } from '@/common/api/interfaces/pagination';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';

export interface SelectOption {
    label: string;
    value: string;
    [key: string]: any;
}

interface SelectFormFieldProps {
    name: string;
    control: any;
    label: string;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    clearable?: boolean;
    renderOption?: (option: SelectOption) => React.ReactNode;
    queryKey: string[];
    queryFn: (context: { pageParam?: number; queryKey: string[] }) => Promise<PageableResponse<SelectOption>>;
    inDialog?: boolean;
}

export const SelectFormField = ({
    name,
    control,
    label,
    placeholder,
    disabled,
    className,
    clearable,
    renderOption,
    queryKey,
    queryFn,
    inDialog,
}: SelectFormFieldProps) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const { ref, inView } = useInView();

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isLoading,
        isError,
    } = useInfiniteQuery({
        queryKey: [...queryKey, search],
        queryFn,
        initialPageParam: 0,
        getNextPageParam: (lastPage) => {
            if (lastPage.last) return null;
            return (lastPage.page ?? 0) + 1;
        },
    });

    // Ефект для скидання та оновлення даних при зміні пошуку
    useEffect(() => {
        const timer = setTimeout(() => {
            if (search !== '') {
                void fetchNextPage();
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [search, fetchNextPage]);

    // Ефект для нескінченного скролу
    useEffect(() => {
        if (inView && hasNextPage && !isFetching) {
            void fetchNextPage();
        }
    }, [inView, hasNextPage, isFetching, fetchNextPage]);

    const allOptions = (data?.pages ?? []).flatMap((page: PageableResponse<SelectOption>) => page.content ?? []);

    const handleSearch = (value: string) => {
        setSearch(value);
    };

    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => {
                const selectedOption = allOptions.find((option: SelectOption) => option?.value === field.value);

                return (
                    <FormItem className="flex flex-col">
                        <FormLabel>{label}</FormLabel>
                        <div className="relative">
                            <Popover open={open} onOpenChange={setOpen}>
                                <PopoverTrigger asChild>
                                    <FormControl>
                                        <Button
                                            variant="outline"
                                            role="combobox"
                                            disabled={disabled}
                                            className={cn(
                                                "h-[42px] w-full justify-between font-normal",
                                                !field.value && "text-muted-foreground",
                                                clearable && field.value && "pr-8",
                                                className
                                            )}
                                        >
                                            {field.value && selectedOption ? (
                                                selectedOption.label
                                            ) : (
                                                placeholder || t('common.select.placeholder')
                                            )}
                                            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </FormControl>
                                </PopoverTrigger>
                                <PopoverContent
                                    className="w-full min-w-[var(--radix-popover-trigger-width)] p-0"
                                    align="start"
                                    side="bottom"
                                    avoidCollisions={!inDialog}
                                    sticky={inDialog ? "always" : "partial"}
                                >
                                    <Command shouldFilter={false}>
                                        <CommandInput
                                            placeholder={t('common.select.search')}
                                            value={search}
                                            onValueChange={handleSearch}
                                            className="h-9 border-b"
                                        />
                                        <CommandList className="max-h-[300px] overflow-auto">
                                            {isError ? (
                                                <CommandEmpty>{t('common.errors.loading')}</CommandEmpty>
                                            ) : allOptions.length === 0 ? (
                                                <CommandEmpty>{t('common.select.noResults')}</CommandEmpty>
                                            ) : (
                                                <CommandGroup>
                                                    {allOptions.map((option: SelectOption) => option && (
                                                        <CommandItem
                                                            key={option.value}
                                                            value={option.value}
                                                            onSelect={() => {
                                                                field.onChange(option.value);
                                                                setOpen(false);
                                                            }}
                                                            className="gap-3"
                                                        >
                                                            <Check
                                                                className={cn(
                                                                    "h-4 w-4 opacity-0",
                                                                    field.value === option.value && "opacity-100"
                                                                )}
                                                            />
                                                            {renderOption ? (
                                                                renderOption(option)
                                                            ) : (
                                                                <span>{option.label}</span>
                                                            )}
                                                        </CommandItem>
                                                    ))}
                                                    {(hasNextPage || isFetching) && (
                                                        <div
                                                            ref={ref}
                                                            className="flex items-center justify-center py-6"
                                                        >
                                                            <Loader2 className="h-4 w-4 animate-spin opacity-50" />
                                                        </div>
                                                    )}
                                                </CommandGroup>
                                            )}
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                            {clearable && field.value && !disabled && (
                                <div
                                    className="absolute top-0 right-0 h-full pr-2 flex items-center"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        field.onChange('');
                                    }}
                                >
                                    <X
                                        className="h-4 w-4 shrink-0 opacity-70 hover:opacity-100 cursor-pointer"
                                    />
                                </div>
                            )}
                        </div>
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
};